import { useEffect, useRef } from 'react';
import { useWebSocketEvents } from '../hooks/use-websocket';
import type { Message, Conversation } from '../api/services/chat.service';

/**
 * WebSocket-based realtime chat subscriptions
 * Replaces Supabase Realtime subscriptions
 */

/**
 * Subscribe to new messages in a conversation
 */
export function useSubscribeToMessages(
  conversationId: string | null,
  onMessage: (message: Message) => void,
  onError?: (error: Error) => void
) {
  const { subscribe, emit, isConnected } = useWebSocketEvents();
  
  // Use refs to store callbacks so they don't trigger re-subscriptions
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
  }, [onMessage, onError]);

  // Join conversation room when conversationId changes
  useEffect(() => {
    if (!conversationId || !isConnected) {
      return;
    }

    let mounted = true;

    // Join conversation
    emit('join_conversation', { conversation_id: conversationId });

    // Subscribe to new messages
    const unsubscribe = subscribe('message:new', (data: Message & { conversation_id: string }) => {
      // Only handle messages for the current conversation
      if (mounted && data.conversation_id === conversationId) {
        onMessageRef.current(data);
      }
    });

    // Subscribe to errors
    const unsubscribeError = onErrorRef.current
      ? subscribe('error', ((error: { message?: string; code?: string }) => {
          onErrorRef.current?.(new Error(error.message || 'WebSocket error'));
        }) as (...args: unknown[]) => void)
      : null;

    return () => {
      mounted = false;
      unsubscribe();
      unsubscribeError?.();
      // Leave conversation
      if (isConnected) {
        emit('leave_conversation', { conversation_id: conversationId });
      }
    };
  }, [conversationId, isConnected, subscribe, emit]);
}

/**
 * Subscribe to conversation updates
 */
export function useSubscribeToConversation(
  conversationId: string | null,
  onUpdate: (conversation: Conversation) => void,
  onError?: (error: Error) => void
) {
  const { subscribe, isConnected } = useWebSocketEvents();
  
  // Use refs to store callbacks
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onErrorRef.current = onError;
  }, [onUpdate, onError]);

  useEffect(() => {
    if (!conversationId || !isConnected) {
      return;
    }

    const unsubscribe = subscribe('conversation:updated', (data: { conversation_id: string } & Partial<Conversation>) => {
      if (data.conversation_id === conversationId) {
        // Transform to Conversation type
        onUpdateRef.current(data as Conversation);
      }
    });

    const unsubscribeError = onErrorRef.current
      ? subscribe('error', ((error: { message?: string; code?: string }) => {
          onErrorRef.current?.(new Error(error.message || 'WebSocket error'));
        }) as (...args: unknown[]) => void)
      : null;

    return () => {
      unsubscribe();
      unsubscribeError?.();
    };
  }, [conversationId, isConnected, subscribe]);
}

/**
 * Subscribe to typing indicators in a conversation
 */
export function useSubscribeToTyping(
  conversationId: string | null,
  currentUserId: string | null,
  onTyping: (userId: string, isTyping: boolean) => void,
  onError?: (error: Error) => void
) {
  const { subscribe, isConnected } = useWebSocketEvents();
  
  // Use refs to store callbacks
  const onTypingRef = useRef(onTyping);
  const onErrorRef = useRef(onError);
  
  useEffect(() => {
    onTypingRef.current = onTyping;
    onErrorRef.current = onError;
  }, [onTyping, onError]);

  useEffect(() => {
    if (!conversationId || !currentUserId || !isConnected) {
      return;
    }

    // Subscribe to typing start
    const unsubscribeStart = subscribe('typing:start', (data: { conversation_id: string; user_id: string }) => {
      if (data.conversation_id === conversationId && data.user_id !== currentUserId) {
        onTypingRef.current(data.user_id, true);
      }
    });

    // Subscribe to typing stop
    const unsubscribeStop = subscribe('typing:stop', (data: { conversation_id: string; user_id: string }) => {
      if (data.conversation_id === conversationId && data.user_id !== currentUserId) {
        onTypingRef.current(data.user_id, false);
      }
    });

    const unsubscribeError = onErrorRef.current
      ? subscribe('error', ((error: { message?: string; code?: string }) => {
          onErrorRef.current?.(new Error(error.message || 'WebSocket error'));
        }) as (...args: unknown[]) => void)
      : null;

    return () => {
      unsubscribeStart();
      unsubscribeStop();
      unsubscribeError?.();
    };
  }, [conversationId, currentUserId, isConnected, subscribe]);
}

/**
 * Subscribe to read receipts for messages
 */
export function useSubscribeToReadReceipts(
  conversationId: string | null,
  onRead: (messageId: string, userId: string) => void
) {
  const { subscribe, isConnected } = useWebSocketEvents();
  
  // Use ref to store callback
  const onReadRef = useRef(onRead);
  
  useEffect(() => {
    onReadRef.current = onRead;
  }, [onRead]);

  useEffect(() => {
    if (!conversationId || !isConnected) {
      return;
    }

    const unsubscribe = subscribe('message:read', (data: { message_id: string; user_id: string; conversation_id: string }) => {
      if (data.conversation_id === conversationId) {
        onReadRef.current(data.message_id, data.user_id);
      }
    });

    return unsubscribe;
  }, [conversationId, isConnected, subscribe]);
}

/**
 * Subscribe to online/offline status (presence) of users
 */
export function useSubscribeToPresence(
  userIds: string[],
  onPresenceChange: (userId: string, isOnline: boolean) => void,
  currentUserId?: string
) {
  const { subscribe, isConnected } = useWebSocketEvents();
  
  // Use ref to store callback
  const onPresenceChangeRef = useRef(onPresenceChange);
  
  useEffect(() => {
    onPresenceChangeRef.current = onPresenceChange;
  }, [onPresenceChange]);

  useEffect(() => {
    if (userIds.length === 0 || !isConnected) {
      return;
    }

    const unsubscribe = subscribe('user:presence', (data: { user_id: string; is_online: boolean }) => {
      if (userIds.includes(data.user_id) && data.user_id !== currentUserId) {
        onPresenceChangeRef.current(data.user_id, data.is_online);
      }
    });

    return unsubscribe;
  }, [userIds, currentUserId, isConnected, subscribe]);
}

/**
 * Subscribe to notifications for new messages
 */
export function useSubscribeToNotifications(
  onNotification: (data: {
    conversation_id: string;
    message_id: string;
    sender_id: string;
    preview: string;
    created_at: string;
  }) => void
) {
  const { subscribe, isConnected } = useWebSocketEvents();
  
  // Use ref to store callback
  const onNotificationRef = useRef(onNotification);
  
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const unsubscribe = subscribe('notification:new_message', ((...args: unknown[]) => {
      const data = args[0] as {
        conversation_id: string;
        message_id: string;
        sender_id: string;
        preview: string;
        created_at: string;
      };
      onNotificationRef.current(data);
    }) as (...args: unknown[]) => void);

    return unsubscribe;
  }, [isConnected, subscribe]);
}
